import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertTriangle, Moon, Wind, Sparkles, ArrowRight, X, HelpCircle, ChevronDown } from "lucide-react";
import { getSessionId } from "../../lib/session";
import { trackEvent } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_BY_ID = {
  deficit_week: AlertTriangle,
  bad_sleep_run: Moon,
  high_stress_run: Wind,
  low_mood_run: Sparkles,
};

const TIER_STYLE = {
  warning: {
    border: "border-amber-400/40",
    bg: "bg-gradient-to-br from-amber-400/10 to-transparent",
    label: "text-amber-300",
    icon: "text-amber-300",
  },
  info: {
    border: "border-[#BEFF38]/25",
    bg: "bg-gradient-to-br from-[#BEFF38]/5 to-transparent",
    label: "text-[#BEFF38]",
    icon: "text-[#BEFF38]",
  },
};

const dismissKey = (id) => `fff_nudge_dismissed_${id}_${new Date().toISOString().slice(0, 10)}`;

/**
 * Reads /api/fuel/context-nudge and renders a single contextual banner when a
 * pattern is tripped (deficit, sleep, stress, mood). Silent otherwise.
 * Dismissable — per-nudge, per-day, so it doesn't nag if the user has already
 * acknowledged today.
 */
export default function ContextualNudgeBanner() {
  const [nudge, setNudge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/fuel/context-nudge`, {
          params: { session_id: getSessionId() },
        });
        const n = r?.data?.nudge;
        if (n) {
          if (localStorage.getItem(dismissKey(n.id)) === "1") {
            setDismissed(true);
          } else {
            setNudge(n);
            trackEvent("fuel_nudge_show", { id: n.id, tier: n.tier });
          }
        }
      } catch { /* silent — enrichment only */ }
      finally { setLoading(false); }
    })();
  }, []);

  const dismiss = () => {
    if (!nudge) return;
    try { localStorage.setItem(dismissKey(nudge.id), "1"); } catch {}
    trackEvent("fuel_nudge_dismiss", { id: nudge.id });
    setDismissed(true);
  };

  if (loading || dismissed || !nudge) return null;

  const Icon = ICON_BY_ID[nudge.id] || AlertTriangle;
  const style = TIER_STYLE[nudge.tier] || TIER_STYLE.info;

  return (
    <div
      data-testid={`fuel-nudge-${nudge.id}`}
      className={`mb-3 rounded-xl border ${style.border} ${style.bg} p-4 relative`}
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss for today"
        data-testid="fuel-nudge-dismiss"
        className="absolute top-2 right-2 p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-2 pr-6">
        <Icon className={`w-4 h-4 ${style.icon} shrink-0 mt-0.5`} strokeWidth={2.5} />
        <div className="min-w-0">
          <p className={`text-[10px] uppercase tracking-[0.16em] font-black ${style.label}`}>
            {nudge.tier === "warning" ? "Warning · pattern detected" : "Fuel nudge · pattern detected"}
          </p>
          <p className="mt-1 text-white text-sm font-bold leading-snug">{nudge.title}</p>
          <p className="mt-1 text-zinc-300 text-xs leading-relaxed">{nudge.body}</p>
          {(nudge.actions || []).length > 0 && (
            <ul className="mt-2 space-y-1 list-disc list-inside text-xs text-zinc-300 leading-relaxed">
              {nudge.actions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
          {nudge.link && (
            <Link
              to={nudge.link}
              onClick={() => trackEvent("fuel_nudge_click", { id: nudge.id })}
              data-testid="fuel-nudge-cta"
              className={`mt-3 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider ${style.label} hover:underline`}
            >
              {nudge.link_label || "Open Fuel"} <ArrowRight className="w-3 h-3" />
            </Link>
          )}

          {nudge.why && (
            <div className="mt-3 border-t border-white/5 pt-2">
              <button
                onClick={() => {
                  setWhyOpen((v) => !v);
                  if (!whyOpen) trackEvent("fuel_nudge_why_open", { id: nudge.id });
                }}
                data-testid="fuel-nudge-why-toggle"
                className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
              >
                <HelpCircle className="w-3 h-3" />
                Why am I seeing this?
                <ChevronDown className={`w-3 h-3 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
              </button>
              {whyOpen && (
                <div data-testid="fuel-nudge-why-panel" className="mt-2 rounded-md bg-black/40 border border-white/5 p-2.5 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Rule</p>
                  <p className="text-[11px] text-zinc-300 leading-relaxed">{nudge.why.rule}</p>
                  {(nudge.why.evidence || []).length > 0 && (
                    <>
                      <p className="mt-2 text-[10px] uppercase tracking-wider font-bold text-zinc-500">Your data</p>
                      <ul className="space-y-0.5">
                        {nudge.why.evidence.map((e, i) => (
                          <li key={i} className="flex items-baseline gap-2 text-[11px]">
                            <span className="text-zinc-500 tabular-nums shrink-0">{e.date}</span>
                            <span className="text-zinc-300">{e.value}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <p className="mt-2 text-[10px] text-zinc-600 leading-snug italic">
                    Disagree with the trigger? Dismiss with the × top-right — it won't show again today.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
